/**
 * Centralized Control Mode Handler
 *
 * Single source of truth for control mode → device command generation
 * and funscript action mapping. Replaces scattered mode checks across
 * manual-control-grid and other editors.
 */

import type { DeviceCommand } from "./device-capabilities"
import type { FunscriptAction } from "./script-store"
import { OCTOGRAPH_ZONES, type ZoneState } from "./zone-utils"

export type ControlMode = "2-axis" | "y-only" | "octograph"

export interface ControlModeConfig {
  mode: ControlMode
  position: { x: number; y: number }
  octographData?: {
    intensity: number
    angle: number
    zoneValues: Record<string, number>
    zoneStates: Record<string, ZoneState>
  }
}

export interface RecordedAction {
  time: number
  x: number
  y: number
  velocity?: number
  zoneValues?: Record<string, number>
}

export class ControlModeHandler {
  /**
   * Generate device commands based on control mode.
   * Single source of truth for mode → command conversion.
   */
  generateCommands(config: ControlModeConfig): DeviceCommand {
    switch (config.mode) {
      case "y-only":
        return {
          position: Math.round(config.position.y),
        }

      case "2-axis":
        return {
          position: Math.round(config.position.y),
          vibration: Math.round(config.position.x),
        }

      case "octograph": {
        if (!config.octographData) {
          return { position: Math.round(config.position.y) }
        }

        const commands: DeviceCommand = {
          position: Math.round(config.octographData.intensity),
        }

        // Add any active zone parameters
        OCTOGRAPH_ZONES.forEach((zone) => {
          if (
            config.octographData!.zoneStates[zone.id] !== "off" &&
            config.octographData!.zoneValues[zone.id] > 0
          ) {
            const value = config.octographData!.zoneValues[zone.id]
            if (zone.parameter === "vibration") {
              commands.vibration = value
            } else if (zone.parameter === "rotation") {
              commands.rotation = value
            } else if (zone.parameter === "air") {
              commands.air = value
            } else if (zone.parameter === "squeeze") {
              commands.squeeze = value
            }
          }
        })

        return commands
      }

      default:
        return { position: Math.round(config.position.y) }
    }
  }

  /**
   * Generate FunscriptActions from recorded actions based on control mode.
   * Handles mode-specific action format (speed, vibration, zone values).
   */
  generateFunscriptActions(
    mode: ControlMode,
    recordedActions: RecordedAction[]
  ): FunscriptAction[] {
    return recordedActions.map((action) => {
      const baseAction: FunscriptAction = {
        at: action.time,
        pos: Math.round(action.y),
      }

      if (mode === "y-only" && action.velocity !== undefined) {
        baseAction.speed = Math.round(action.velocity)
      } else if (mode === "2-axis") {
        baseAction.vibration = Math.round(action.x)
      } else if (mode === "octograph" && action.zoneValues) {
        Object.entries(action.zoneValues).forEach(([zoneId, value]) => {
          const zone = OCTOGRAPH_ZONES.find((z) => z.id === zoneId)
          if (zone && value > 0) {
            ;(baseAction as unknown as Record<string, unknown>)[zone.parameter] = Math.round(value)
          }
        })
      }

      return baseAction
    })
  }
}

export const controlModeHandler = new ControlModeHandler()
